<template>
    <div class="calendar-wrap">
        <!-- toolbar -->
        <q-toolbar
            class="tool-bar fit row wrap justify-between items-start content-center"
        >
            <!-- left buttons: month, week and day -->
            <div>
                <q-btn
                    :class="{ 'text-primary': view === 'month' }"
                    flat
                    @click="view = 'month'"
                >
                    {{ $t('general.month') }}
                </q-btn>

                <q-btn
                    :class="{ 'text-primary': view === 'week' }"
                    flat
                    @click="view = 'week'"
                >
                    {{ $t('general.week') }}
                </q-btn>

                <q-btn
                    :class="{ 'text-primary': view === 'day' }"
                    flat
                    @click="view = 'day'"
                >
                    {{ $t('general.day') }}
                </q-btn>
            </div>
            <!-- right buttons: today, month and year selectors -->
            <div
                class="row wrap justify-between items-center content-center date-selectors"
            >
                <!-- today -->
                <q-btn
                    dense
                    flat
                    class="direction-button q-mx-sm"
                    style="height: 100%"
                    @click="goToToday"
                >
                    {{ $t('actions.view_today') }}
                </q-btn>

                <q-separator
                    vertical
                    class="q-mx-sm"
                    size="1px"
                    color="primary"
                >
                </q-separator>

                <!-- month -->
                <div class="col-auto row justify-center items-center">
                    <q-btn
                        dense
                        flat
                        size="10px"
                        icon="arrow_back_ios"
                        class="direction-button btn-custom q-mx-sm"
                        style="height: 100%"
                        :ripple="false"
                        @click="onPrevMonth"
                    >
                    </q-btn>

                    <div class="col-auto text-uppercase">
                        {{ monthFormatter(parsedMonth) }}
                    </div>

                    <q-btn
                        dense
                        flat
                        size="10px"
                        icon="arrow_forward_ios"
                        class="direction-button btn-custom q-mx-sm"
                        style="height: 100%"
                        :ripple="false"
                        @click="onNextMonth"
                    >
                    </q-btn>
                </div>

                <q-separator
                    vertical
                    class="q-mx-sm"
                    size="1px"
                    color="primary"
                >
                </q-separator>

                <!-- year -->
                <div class="col-auto row justify-center items-center">
                    <q-btn
                        dense
                        flat
                        size="10px"
                        icon="arrow_back_ios"
                        class="direction-button btn-custom q-mx-sm"
                        style="height: 100%"
                        :ripple="false"
                        @click="onPrevYear"
                    >
                    </q-btn>

                    <div class="col-auto text-uppercase">
                        {{ parsedMonth.year }}
                    </div>

                    <q-btn
                        dense
                        flat
                        size="10px"
                        icon="arrow_forward_ios"
                        class="direction-button btn-custom q-mx-sm"
                        style="height: 100%"
                        :ripple="false"
                        @click="onNextYear"
                    >
                    </q-btn>
                </div>
            </div>
        </q-toolbar>

        <!-- q-calendar navigation bar -->
        <div class="title-bar row items-center overflow-hidden">
            <!-- previous button -->
            <q-btn
                flat
                icon="arrow_back_ios"
                class="direction-button"
                style="height: 100%"
                @click="arrowPrev"
            />
            <transition :name="transition" appear>
                <div
                    class="row justify-between items-center text-white overflow-hidden"
                    style="width: calc(100% - 112px)"
                >
                    <!-- month toolbar content -->
                    <template v-if="view === 'month'">
                        <div
                            class="title-month col column items-center justify-center text-uppercase text-black"
                            :class="monthClass()"
                        >
                            <span class="text-subtitle1">{{
                                parsedMonth.year
                            }}</span>
                            <span class="text-h5">{{
                                monthFormatter(parsedMonth)
                            }}</span>
                        </div>
                    </template>
                    <!-- week toolbar content -->
                    <template
                        v-if="view === 'week' || view === 'week-scheduler'"
                    >
                        <div
                            v-for="day in days"
                            :key="day.date"
                            class="col-auto column align-center"
                            :style="dayStyle"
                        >
                            <!-- <q-btn
                            flat
                            class="title-day"
                            :class="dayClass(day)"
                            color="black"
                            @click="currentDate = day.date; transition = ''"
                        > -->
                            <div
                                class="title-week text-uppercase text-black text-subtitle2"
                                :class="weekClass(day)"
                            >
                                <div class="text-center" style="width: 100%">
                                    {{ monthFormatter(day, true) }}
                                </div>
                                <div
                                    class="text-center text-bold"
                                    style="width: 100%; font-size: 16px"
                                >
                                    {{ dayFormatter(day, false) }}
                                </div>
                                <div
                                    class="text-center"
                                    style="width: 100%; font-size: 10px"
                                >
                                    {{ weekdayFormatter(day, true) }}
                                </div>
                                <div
                                    v-for="(event, index) in hasLessons[
                                        day.date
                                    ]"
                                    :key="index"
                                >
                                    <div class="has-lessons"></div>
                                </div>
                            </div>
                            <!-- </q-btn> -->
                        </div>
                    </template>
                    <!-- day toolbar content -->
                    <template v-if="view === 'day'">
                        <div
                            class="title-day col column items-center justify-center text-uppercase text-black"
                            :class="dayClass()"
                        >
                            <span class="text-subtitle1">{{
                                dayFormatter(parsedDay, false)
                            }}</span>
                            <span class="text-h5">{{
                                weekdayFormatter(parsedDay, true)
                            }}</span>
                            <div
                                v-if="hasLessons[parsedDay.date]"
                                class="has-lessons"
                            ></div>
                        </div>
                    </template>
                </div>
            </transition>
            <!-- next button -->
            <q-btn
                flat
                icon="arrow_forward_ios"
                class="direction-button"
                style="height: 100%"
                @click="arrowNext"
            />
        </div>

        <q-calendar
            ref="calendar"
            v-model="currentDate"
            class="calendar-view"
            :style="theme"
            :view="view"
            :locale="locale.key"
            :interval-height="50"
            :interval-minutes="30"
            :interval-count="32"
            :interval-start="16"
            :resources="view === 'week-scheduler' ? resources : null"
            :resource-height="400"
            :resource-width="56"
            hour24-format
            hide-header
            animated
            no-scroll
            no-active-date
        >
            <!-- Badge display for lessons in week/day view -->
            <template
                v-slot:day-body="{
                    timestamp,
                    timeStartPos,
                    timeDurationHeight,
                }"
            >
                <div
                    v-for="(event, index) in getEvents(
                        timestamp.date,
                        lessonsAux
                    )"
                    :key="index"
                >
                    <q-badge
                        class="my-event justify-center ellipsis badge-week-day-view"
                        :class="badgeClasses(event, 'body')"
                        :style="
                            badgeStyles(
                                event,
                                'body',
                                timeStartPos,
                                timeDurationHeight
                            )
                        "
                    >
                        <div class="row items-center q-col-gutter-x-sm">
                            <q-icon
                                v-if="event.icon"
                                :name="event.icon"
                                class="col-auto"
                            ></q-icon>

                            <span class="col ellipsis">
                                {{ event.classrooms.code }}
                            </span>
                        </div>

                        <span
                            v-if="event.classrooms.businessUnit"
                            class="text-italic"
                        >
                            {{ event.classrooms.businessUnit.name }}
                        </span>

                        <div v-if="event.isLesson" class="q-my-sm">
                            {{ $t('general.start') }}:

                            <span class="text-bold">{{ event.time }}</span>

                            <br />

                            {{ $t('general.end') }}:

                            <span class="text-bold">{{ event.timeEnd }}</span>
                        </div>

                        <div v-if="event.classrooms" class="q-my-sm">
                            {{ $tc('general.classrooms', 1) }}:
                            <span class="text-bold">
                                {{ event.classrooms.name }}
                            </span>
                        </div>

                        <div v-if="event.isLesson" class="q-my-sm">
                            <div
                                v-for="(trainer, i) in event.lessonTrainers"
                                :key="i"
                                class="ellipsis"
                            >
                                <q-badge
                                    :color="
                                        trainerBadgeColor(
                                            trainer.trainerRoles.id
                                        )
                                    "
                                >
                                    {{
                                        getTranslationFromJSONString(
                                            trainer.trainerRoles.title,
                                            locale.key
                                        )
                                    }}
                                </q-badge>

                                <span class="text-bold q-mr-sm">
                                    {{
                                        trainer.trainers
                                            ? `${trainer.trainers.user.first_name}`
                                            : `${trainer.user.first_name}`
                                    }}
                                </span>
                            </div>
                        </div>

                        <q-tooltip
                            anchor="center middle"
                            self="bottom left"
                            content-class="text-body2 text-left"
                        >
                            <div class="row items-center q-col-gutter-x-sm">
                                <q-icon
                                    v-if="event.icon"
                                    :name="event.icon"
                                    class="col-auto"
                                >
                                </q-icon>

                                <span class="col ellipsis">
                                    {{
                                        `${event.classrooms.name} [${event.classrooms.code}]`
                                    }}
                                </span>
                            </div>

                            <div
                                v-if="event.classrooms.businessUnit"
                                class="row items-center"
                            >
                                <span class="text-italic">{{
                                    event.classrooms.businessUnit.name
                                }}</span>
                            </div>

                            <div v-if="event.courseName" class="col ellipsis">
                                {{
                                    `${$tc('general.courses', 1)}: ${
                                        event.courseName
                                    }`
                                }}
                            </div>

                            <div class="q-my-sm">
                                <div class="text-bold">
                                    {{ $t('general.start') }}:
                                    {{
                                        event.isLesson ? event.time : event.date
                                    }}
                                </div>

                                <div class="text-bold">
                                    {{ $t('general.end') }}:
                                    {{
                                        event.isLesson
                                            ? event.timeEnd
                                            : event.dateEnd
                                    }}
                                </div>
                            </div>

                            <div v-if="event.classrooms" class="q-my-sm">
                                {{ $tc('general.classrooms', 1) }}:
                                <span class="text-bold">{{
                                    event.classrooms.name
                                }}</span>
                            </div>

                            <div v-if="event.isLesson" class="q-my-sm">
                                <div
                                    v-for="(trainer, i) in event.lessonTrainers"
                                    :key="i"
                                    class="ellipsis"
                                >
                                    <q-badge
                                        :color="
                                            trainerBadgeColor(
                                                trainer.trainerRoles.id
                                            )
                                        "
                                    >
                                        {{
                                            getTranslationFromJSONString(
                                                trainer.trainerRoles.title,
                                                locale.key
                                            )
                                        }}
                                    </q-badge>

                                    <span class="text-bold q-mr-sm">
                                        {{
                                            trainer.trainers
                                                ? `${trainer.trainers.user.first_name}`
                                                : `${trainer.user.first_name}`
                                        }}
                                    </span>
                                </div>
                            </div>
                        </q-tooltip>
                    </q-badge>
                </div>
            </template>

            <!-- Badge display for lessons in month view -->
            <template v-slot:day="{ timestamp }">
                <div
                    v-for="(event, index) in getEvents(
                        timestamp.date,
                        lessonsAux
                    )"
                    :key="index"
                >
                    <q-badge
                        class="badge-month-view"
                        :class="badgeClasses(event, 'day')"
                        :style="badgeStyles(event, 'day')"
                    >
                        <div class="row items-center q-col-gutter-x-sm">
                            <q-icon
                                v-if="event.icon"
                                :name="event.icon"
                                class="col-auto"
                            >
                            </q-icon>

                            <span class="col ellipsis">
                                {{ event.classrooms.code }}
                            </span>
                            <span class="text-bold">
                                {{ event.time }} - {{ event.timeEnd }}
                            </span>
                        </div>

                        <q-tooltip
                            anchor="top middle"
                            self="center middle"
                            content-class="text-body2 text-left"
                        >
                            <div class="row items-center">
                                <q-icon
                                    v-if="event.icon"
                                    :name="event.icon"
                                    class="q-mr-xs"
                                >
                                </q-icon>

                                <span class="ellipsis">
                                    {{
                                        `${event.classrooms.name} [${event.classrooms.code}]`
                                    }}
                                </span>
                            </div>

                            <span
                                v-if="event.classrooms.businessUnit"
                                class="text-italic"
                            >
                                {{ event.classrooms.businessUnit.name }}
                            </span>

                            <div class="q-my-sm">
                                {{ $t('general.start') }}:

                                <span class="text-bold">
                                    {{ `${event.date} ${event.time}` }}
                                </span>

                                <br />

                                {{ $t('general.end') }}:

                                <span class="text-bold">
                                    {{ `${event.dateEnd} ${event.timeEnd}` }}
                                </span>
                            </div>

                            <div v-if="event.lessonTrainers" class="q-my-sm">
                                <div
                                    v-for="(trainer, i) in event.lessonTrainers"
                                    :key="i"
                                    class="ellipsis"
                                >
                                    <q-badge
                                        :color="
                                            trainerBadgeColor(
                                                trainer.trainerRoles.id
                                            )
                                        "
                                    >
                                        {{
                                            getTranslationFromJSONString(
                                                trainer.trainerRoles.title,
                                                locale.key
                                            )
                                        }}
                                    </q-badge>

                                    <span class="text-bold q-mr-sm">
                                        {{
                                            trainer.trainers
                                                ? `${trainer.trainers.user.first_name}`
                                                : `${trainer.user.first_name}`
                                        }}
                                    </span>
                                </div>
                            </div>
                            <div v-if="event.trainee_number" class="q-my-sm">
                                {{ $t('classes.attendees') }}:
                                <span class="text-bold">{{
                                    event.trainee_number
                                }}</span>
                            </div>
                        </q-tooltip>
                    </q-badge>
                </div>
            </template>

            <!-- Badge display for scheduler -->
            <template v-slot:scheduler-resource-day="{ timestamp /*index,*/ }">
                <div
                    v-for="(event, index) in getEvents(
                        timestamp.date,
                        lessonsAux
                    )"
                    :key="index"
                >
                    <q-badge
                        v-if="event.time"
                        class="my-event justify-center schedule-lesson-view badge-scheduler-view"
                        :class="badgeClasses(event, 'body')"
                        :style="badgeStyles(event, 'body')"
                    >
                        <div class="row items-center q-col-gutter-x-sm">
                            <q-icon
                                v-if="event.icon"
                                :name="event.icon"
                                class="col-auto"
                            ></q-icon>
                            <span class="col ellipsis">{{
                                `${event.classrooms.name} [${event.classrooms.code}]`
                            }}</span>
                        </div>
                        <span
                            v-if="event.classrooms.businessUnit"
                            class="text-italic"
                            >{{ event.classrooms.businessUnit.name }}</span
                        >
                        <div class="q-my-sm">
                            {{ $t('general.start') }}:
                            <span class="text-bold">{{ event.time }}</span
                            ><br />
                            {{ $t('general.end') }}:
                            <span class="text-bold">{{ event.timeEnd }}</span>
                        </div>

                        <div v-if="event.course" class="q-my-sm">
                            {{ $tc('general.courses', 1) }}:
                            <span class="text-bold">{{
                                getTranslationFromJSONString(
                                    event.course.title,
                                    locale.key
                                )
                            }}</span>
                        </div>
                        <div v-if="event.classrooms" class="q-my-sm">
                            {{ $tc('general.classrooms', 1) }}:
                            <span class="text-bold">{{
                                event.classrooms.name
                            }}</span>
                        </div>
                        <div class="q-my-sm">
                            <div
                                v-for="(trainer, i) in event.lessonTrainers"
                                :key="i"
                                class="ellipsis"
                            >
                                <q-badge
                                    :color="
                                        trainerBadgeColor(
                                            trainer.trainerRoles.id
                                        )
                                    "
                                >
                                    {{
                                        getTranslationFromJSONString(
                                            trainer.trainerRoles.title,
                                            locale.key
                                        )
                                    }}
                                </q-badge>

                                <span class="text-bold q-mr-sm">
                                    {{
                                        trainer.trainers
                                            ? `${trainer.trainers.user.first_name}`
                                            : `${trainer.user.first_name}`
                                    }}
                                </span>
                            </div>
                        </div>
                        <q-tooltip
                            anchor="center middle"
                            self="bottom left"
                            content-class="text-body2 text-left"
                        >
                            <div class="row items-center q-col-gutter-x-sm">
                                <q-icon
                                    v-if="event.icon"
                                    :name="event.icon"
                                    class="col-auto"
                                ></q-icon>
                                <span class="col ellipsis">{{
                                    `${event.classrooms.name} [${event.classrooms.code}]`
                                }}</span>
                            </div>
                            <span
                                v-if="event.classrooms.businessUnit"
                                class="text-italic"
                                >{{ event.classrooms.businessUnit.name }}</span
                            >
                            <div class="q-my-sm">
                                {{ $t('general.start') }}:
                                <span class="text-bold">{{ event.time }}</span
                                ><br />
                                {{ $t('general.end') }}:
                                <span class="text-bold">{{
                                    event.timeEnd
                                }}</span>
                            </div>

                            <div v-if="event.course" class="q-my-sm">
                                {{ $tc('general.courses', 1) }}:
                                <span class="text-bold">{{
                                    getTranslationFromJSONString(
                                        event.course.title,
                                        locale.key
                                    )
                                }}</span>
                            </div>
                            <div v-if="event.classrooms" class="q-my-sm">
                                {{ $tc('general.classrooms', 1) }}:
                                <span class="text-bold">{{
                                    event.classrooms.name
                                }}</span>
                            </div>
                            <div class="q-my-sm">
                                <div
                                    v-for="(trainer, i) in event.lessonTrainers"
                                    :key="i"
                                    class="ellipsis"
                                >
                                    <q-badge
                                        :color="
                                            trainerBadgeColor(
                                                trainer.trainerRoles.id
                                            )
                                        "
                                    >
                                        {{
                                            getTranslationFromJSONString(
                                                trainer.trainerRoles.title,
                                                locale.key
                                            )
                                        }}
                                    </q-badge>

                                    <span class="text-bold q-mr-sm">
                                        {{
                                            trainer.trainers
                                                ? `${trainer.trainers.user.first_name}`
                                                : `${trainer.user.first_name}`
                                        }}
                                    </span>
                                </div>
                            </div>
                        </q-tooltip>
                    </q-badge>
                </div>
            </template>
        </q-calendar>
    </div>
</template>

<script>
import translate from '@/local-shared/mixins/translate'
import notify from '@/local-shared/mixins/notify'
import QCalendar from '@quasar/quasar-ui-qcalendar'
import formatDate from '@/helpers/date-time'
import moment from 'moment'
import store from '@/store'
import _ from 'lodash'

// const loadingKeys = {
//     fetchingEnrollments: 'fetching-enrollments',
// }

const CURRENT_DAY = new Date()

function getCurrentDay(day) {
    const newDay = new Date(CURRENT_DAY)
    newDay.setDate(day)
    const tm = QCalendar.parseDate(newDay)
    return tm.date
}

const reRGBA = /^\s*rgb(a)?\s*\((\s*(\d+)\s*,\s*?){2}(\d+)\s*,?\s*([01]?\.?\d*?)?\s*\)\s*$/

function textToRgb(color) {
    if (typeof color !== 'string') {
        throw new TypeError('Expected a string')
    }

    const m = reRGBA.exec(color)
    if (m) {
        const rgb = {
            r: Math.min(255, parseInt(m[2], 10)),
            g: Math.min(255, parseInt(m[3], 10)),
            b: Math.min(255, parseInt(m[4], 10)),
        }
        if (m[1]) {
            rgb.a = Math.min(1, parseFloat(m[5]))
        }
        return rgb
    }
    return hexToRgb(color)
}

function hexToRgb(hex) {
    if (typeof hex !== 'string') {
        throw new TypeError('Expected a string')
    }

    hex = hex.replace(/^#/, '')

    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
    } else if (hex.length === 4) {
        hex =
            hex[0] +
            hex[0] +
            hex[1] +
            hex[1] +
            hex[2] +
            hex[2] +
            hex[3] +
            hex[3]
    }

    const num = parseInt(hex, 16)

    return hex.length > 6
        ? {
              r: (num >> 24) & 255,
              g: (num >> 16) & 255,
              b: (num >> 8) & 255,
              a: Math.round((num & 255) / 2.55),
          }
        : { r: num >> 16, g: (num >> 8) & 255, b: num & 255 }
}

function luminosity(color) {
    if (typeof color !== 'string' && (!color || color.r === undefined)) {
        throw new TypeError('Expected a string or a {r, g, b} object as color')
    }

    const rgb = typeof color === 'string' ? textToRgb(color) : color,
        r = rgb.r / 255,
        g = rgb.g / 255,
        b = rgb.b / 255,
        R = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4),
        G = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4),
        B = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)
    return 0.2126 * R + 0.7152 * G + 0.0722 * B
}

export default {
    name: 'LessonsCalendarComponent',

    mixins: [translate, notify],

    props: {
        classes: {
            type: Array,
            default: () => [],
        },
        lessons: {
            type: Array,
            default: () => [],
        },
        filter: {
            type: Object,
            default: () => ({
                business_units: null,
            }),
        },
        businessUnits: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            view: 'day',
            currentDate: getCurrentDay(CURRENT_DAY.getDate()),
            theme: {
                '--calendar-border-current': '#dc241f 2px solid',
                '--calendar-current-color': '#dc241f',
                '--calendar-active-date-color': '#fff',
            },

            // scheduler
            resources: [{ label: '' }],

            // Custom header
            monthFormatter: '',
            dayFormatter: '',
            weekdayFormatter: '',
            weekdays: [0, 1, 2, 3, 4, 5, 6],
            months: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
            transitionPrev: 'slide-right',
            transitionNext: 'slide-left',
            transition: '',

            eventTypeToShow: 'lessons',
        }
    },

    computed: {
        // Custom header
        locale() {
            return store.getters['locale/currentLocale']
        },

        weekdaySkips() {
            return QCalendar.getWeekdaySkips(this.weekdays)
        },

        parsedDay() {
            if (this.currentDate) {
                return QCalendar.parseTimestamp(this.currentDate)
            }
            return undefined
        },

        parsedMonth() {
            if (this.currentDate) {
                return QCalendar.getStartOfMonth(
                    QCalendar.parseTimestamp(this.currentDate)
                )
            }
            return undefined
        },

        parsedStart() {
            if (this.currentDate) {
                return QCalendar.getStartOfWeek(
                    QCalendar.parseTimestamp(this.currentDate),
                    this.weekdays,
                    this.today
                )
            }
            return undefined
        },

        parsedEnd() {
            if (this.currentDate) {
                return QCalendar.getEndOfWeek(
                    QCalendar.parseTimestamp(this.currentDate),
                    this.weekdays,
                    this.today
                )
            }
            return undefined
        },

        today() {
            const newDay = new Date(CURRENT_DAY)
            const tm = QCalendar.parseDate(newDay)
            return tm
        },

        days() {
            if (this.parsedStart && this.parsedEnd) {
                return QCalendar.createDayList(
                    this.parsedStart,
                    this.parsedEnd,
                    this.today,
                    this.weekdaySkips
                )
            }
            return []
        },

        dayStyle() {
            return {
                width: 100 / this.weekdays.length + '%',
            }
        },

        // Map classes
        classesAux() {
            const classes = []
            this.classes.forEach((classItem) => {
                classes.push(
                    _.merge(classItem, {
                        date: formatDate.fromServerToVDatePicker(
                            classItem.start_date
                        ),
                        time: formatDate.fromServerToUxTime(
                            classItem.start_date
                        ),
                        dateEnd: formatDate.fromServerToVDatePicker(
                            classItem.end_date
                        ),
                        timeEnd: formatDate.fromServerToUxTime(
                            classItem.end_date
                        ),
                        duration: this.eventDuration(classItem),
                        // bgcolor: '#f1f1f1',
                        bgcolor:
                            '#' +
                            ((Math.random() * 0xffffff) << 0).toString(16),
                        icon: 'school',
                        days: this.eventDuration(classItem, 'days'),
                        courseName: classItem.courses?.title
                            ? this.getTranslationFromJSONString(
                                  classItem.courses.title,
                                  this.locale.key
                              )
                            : '',
                        isClass: true,
                    })
                )
            })
            return classes
        },

        // Map lessons
        lessonsAux() {
            const lessons = []

            this.lessons.forEach((lesson) =>
                lessons.push(
                    _.merge(lesson, {
                        date: formatDate.fromServerToVDatePicker(
                            lesson.start_date
                        ),
                        time: formatDate.fromServerToUxTime(lesson.start_date),
                        dateEnd: formatDate.fromServerToVDatePicker(
                            lesson.end_date
                        ),
                        timeEnd: formatDate.fromServerToUxTime(lesson.end_date),
                        duration: this.eventDuration(lesson),
                        // attendees: this.eventAttendees(lesson.class_id),
                        bgcolor: '#f1f1f1',
                        icon: 'fas fa-chalkboard-teacher',
                        isLesson: true,
                    })
                )
            )

            let uniqueLessons = [
                ...new Map(lessons.map((item) => [item['id'], item])).values(),
            ]

            return uniqueLessons
        },

        hasLessons() {
            // convert the lessons into a map of events keyed by date
            const map = {}
            this.lessonsAux.forEach((event) =>
                (map[event.date] = map[event.date] || []).push(event)
            )
            return map
        },
    },

    async created() {
        this.monthFormatter = this.monthFormatterFunc()
        this.dayFormatter = this.dayFormatterFunc()
        this.weekdayFormatter = this.weekdayFormatterFunc()
    },

    methods: {
        // Custom week header
        goToToday() {
            const newDay = new Date(CURRENT_DAY)
            const ts = QCalendar.parseDate(newDay)
            this.currentDate = ts.date
            this.$refs.calendar.updateCurrent(this.currentDate)
        },

        onPrevYear() {
            const ts = QCalendar.addToDate(this.parsedStart, { year: -1 })
            this.currentDate = ts.date
            this.$refs.calendar.updateCurrent(this.currentDate)
        },

        onNextYear() {
            const ts = QCalendar.addToDate(this.parsedStart, { year: 1 })
            this.currentDate = ts.date
            this.$refs.calendar.updateCurrent(this.currentDate)
        },

        onPrevMonth() {
            const ts = QCalendar.addToDate(this.parsedMonth, { month: -1 })
            this.currentDate = ts.date
            this.$refs.calendar.updateCurrent(this.currentDate)
        },

        onNextMonth() {
            const ts = QCalendar.addToDate(this.parsedMonth, { month: 1 })
            this.currentDate = ts.date
            this.$refs.calendar.updateCurrent(this.currentDate)
        },

        onPrevWeek() {
            const ts = QCalendar.addToDate(this.parsedStart, { day: -7 })
            this.currentDate = ts.date
            this.transition = 'q-transition--' + this.transitionPrev
        },

        onNextWeek() {
            const ts = QCalendar.addToDate(this.parsedStart, { day: 7 })
            this.currentDate = ts.date
            this.transition = 'q-transition--' + this.transitionNext
        },

        onPrev() {
            const ts = QCalendar.addToDate(this.parsedDay, { day: -1 })
            this.currentDate = ts.date
            this.transition = 'q-transition--' + this.transitionPrev
        },

        onNext() {
            const ts = QCalendar.addToDate(this.parsedDay, { day: 1 })
            this.currentDate = ts.date
            this.transition = 'q-transition--' + this.transitionNext
        },

        arrowPrev() {
            if (this.view === 'day') {
                this.onPrev()
            } else if (this.view === 'week' || this.view === 'week-scheduler') {
                this.onPrevWeek()
            } else if (this.view === 'month') {
                this.onPrevMonth()
            }
        },

        arrowNext() {
            if (this.view === 'day') {
                this.onNext()
            } else if (this.view === 'week' || this.view === 'week-scheduler') {
                this.onNextWeek()
            } else if (this.view === 'month') {
                this.onNextMonth()
            }
        },

        dayClass() {
            return {
                'selected-date': this.today.date === this.currentDate,
            }
        },

        weekClass(day) {
            return {
                row: true,
                'justify-center': true,
                'selected-date': this.today.date === day.date,
            }
        },

        monthClass() {
            let month = this.parsedMonth.month
            let year = this.parsedMonth.year

            return {
                'selected-date':
                    this.today.month === month && this.today.year === year,
            }
        },

        yearFormatterFunc() {
            const longOptions = { timeZone: 'UTC', year: 'long' }
            const shortOptions = { timeZone: 'UTC', year: 'short' }

            return QCalendar.createNativeLocaleFormatter(
                this.locale.key,
                (_tms, short) => (short ? shortOptions : longOptions)
            )
        },

        monthFormatterFunc() {
            const longOptions = { timeZone: 'UTC', month: 'long' }
            const shortOptions = { timeZone: 'UTC', month: 'short' }

            return QCalendar.createNativeLocaleFormatter(
                this.locale.key,
                (_tms, short) => (short ? shortOptions : longOptions)
            )
        },

        weekdayFormatterFunc() {
            const longOptions = { timeZone: 'UTC', weekday: 'long' }
            const shortOptions = { timeZone: 'UTC', weekday: 'short' }

            return QCalendar.createNativeLocaleFormatter(
                this.locale.key,
                (_tms, short) => (short ? shortOptions : longOptions)
            )
        },

        dayFormatterFunc() {
            const longOptions = { timeZone: 'UTC', day: '2-digit' }
            const shortOptions = { timeZone: 'UTC', day: 'numeric' }

            return QCalendar.createNativeLocaleFormatter(
                this.locale.key,
                (_tms, short) => (short ? shortOptions : longOptions)
            )
        },

        // Fetch classes
        eventDuration(event, timeType = 'minutes') {
            var b = moment(event.start_date)
            var a = moment(event.end_date)
            return a.diff(b, timeType)
        },

        isCssColor(color) {
            return !!color && !!color.match(/^(#|(rgb|hsl)a?\()/)
        },

        trainerBadgeColor(roleId) {
            return roleId === 1 ? 'blue-10' : 'orange-8'
        },

        badgeClasses(event, type) {
            const cssColor = this.isCssColor(event.bgcolor)
            const isHeader = type === 'header'
            return {
                [`text-white bg-${event.bgcolor}`]: !cssColor,
                'full-width':
                    !isHeader && (!event.side || event.side === 'full'),
                'left-side': !isHeader && event.side === 'left',
                'right-side': !isHeader && event.side === 'right',
            }
        },

        badgeStyles(event, type, timeStartPos, timeDurationHeight) {
            const s = {}
            if (this.isCssColor(event.bgcolor)) {
                s['background-color'] = event.bgcolor
                s.color = luminosity(event.bgcolor) > 0.5 ? 'black' : 'white'
            }

            if (timeStartPos) {
                s.top = timeStartPos(event.time) + 'px'
            }

            if (timeDurationHeight) {
                s.height = timeDurationHeight(event.duration) + 'px'
            }

            s['align-items'] = 'flex-start'
            return s
        },

        getEvents(dt, array) {
            if (!array || !array.length) {
                return []
            }

            const currentDate = QCalendar.parsed(dt)
            const events = []

            for (let i = 0; i < array.length; ++i) {
                let added = false
                const event = array[i]

                if (event.date === dt) {
                    if (event.time) {
                        if (events.length > 0) {
                            // check for overlapping times
                            const startTime = QCalendar.parsed(
                                event.date + ' ' + event.time
                            )
                            const endTime = QCalendar.addToDate(startTime, {
                                minute: event.duration,
                            })
                            for (let j = 0; j < events.length; ++j) {
                                if (events[j].time) {
                                    const startTime2 = QCalendar.parsed(
                                        events[j].date + ' ' + events[j].time
                                    )
                                    const endTime2 = QCalendar.addToDate(
                                        startTime2,
                                        { minute: events[j].duration }
                                    )
                                    if (
                                        QCalendar.isBetweenDates(
                                            startTime,
                                            startTime2,
                                            endTime2
                                        ) ||
                                        QCalendar.isBetweenDates(
                                            endTime,
                                            startTime2,
                                            endTime2
                                        )
                                    ) {
                                        events[j].side = 'left'
                                        event.side = 'right'
                                        events.push(event)
                                        added = true
                                        break
                                    }
                                }
                            }
                        }
                    }
                    if (!added) {
                        event.side = undefined
                        events.push(event)
                    }
                } else if (event.days) {
                    // check for overlapping dates
                    const startDate = QCalendar.parsed(event.date)
                    const endDate = QCalendar.addToDate(startDate, {
                        day: event.days,
                    })
                    if (
                        QCalendar.isBetweenDates(
                            currentDate,
                            startDate,
                            endDate
                        )
                    ) {
                        events.push(event)
                        added = true
                    }
                }
            }
            return events
        },
    },
}
</script>
